import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { messages } from '../../../_shared/i18n/messages';

import TextEditor from '../TextEditor';
import TextEditorInline from '../TextEditorInline';

import './TextEditorInput.scss';

const propTypes = {

	/**
	 * The input id. If none is provided, the input name is used.
	 */
	id: PropTypes.string,

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input label.
	 */
	label: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.element,
		PropTypes.array,
		PropTypes.shape({}),
	]).isRequired,

	/**
	 * The input value, i.e. a JSON-serialized ProseMirror Document object.
	 */
	value: PropTypes.shape({}),

	/**
	 * The input placeholder text.
	 */
	placeholder: PropTypes.string,

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * True, if using the inline version of the text editor.
	 */
	isInline: PropTypes.bool,

	/**
	 * True, if the input value is disabled.
	 */
	disabled: PropTypes.bool,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func.isRequired,

};

const TextEditorInput = (props) => {

	const {
		id,
		name,
		label,
		value,
		placeholder,
		isEditing,
		isInvalid,
		isInline,
		// disabled,
		onChange,
		...restProps
	} = props;

	// TODO: Add support for disabling the text editor

	const editorDomId = useMemo(() => id || `${name}-${uuid()}`, [id, name]);

	if (!isEditing) {

		return (
			<div className="tc-material-input-texteditor-display">
				{!value &&
					<span className="tc-material-input-display-empty-value">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_TEXT_EMPTY_VALUE} />
					</span>
				}
				{value && isInline &&
					<TextEditorInline
						{...restProps}
						editorDomId={editorDomId}
						content={value}
						readOnly />
				}
				{value && !isInline &&
					<TextEditor
						{...restProps}
						editorDomId={editorDomId}
						content={value}
						readOnly />
				}
			</div>
		);

	}

	if (isInline) {

		return (
			<TextEditorInline
				{...restProps}
				className={`tc-material-input-texteditor ${isInvalid ? 'tc-material-input-invalid' : ''}`}
				editorDomId={editorDomId}
				content={value}
				placeholderText={placeholder || label}
				onChange={onChange} />
		);

	}

	return (
		<TextEditor
			{...restProps}
			className={`tc-material-input-texteditor ${isInvalid ? 'tc-material-input-invalid' : ''}`}
			innerClassName="p-3"
			editorDomId={editorDomId}
			content={value}
			placeholderText={placeholder || label}
			onChange={onChange} />
	);

};

TextEditorInput.propTypes = propTypes;

export default TextEditorInput;
